import React, { FC } from 'react';
import { useDispatch, useSelector } from "react-redux";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import produce from 'immer';
import { IApplication } from '../../Interfaces/Application/IApplication';
import { getCustomFieldAllowNull, getApplicant } from '../../utils/appUtils';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { ApplicationBudget, ApplicationBudgetRow, onModifyApplicationBudget } from '../../Data/ApplicationBudgetData';
import { ApplicationDispatch } from '../../store/application/types';
import { updateBudget, updateEditingApp } from '../../store/application/actions';
import { useBusinessRulesEngine } from '../../Data/BusinessRulesData';
import { NumberFormatCustom } from '../Shared/NumberFormatCustom';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import { getIncomeAndDebtBreakdownInternal } from '../../utils/quoteUtils';
import { useConfiguration } from "../../Data/ConfigurationData";
import { isNotNil } from 'ramda';
import { AppState } from '../../store/rootReducer';
import {MonthlyIncomeCalculator, subtract} from '@amsanalytics/react-shared-library';
import { useDialog } from '../../providers/DialogProvider';
import { IIncomePeriod } from '../../Interfaces/Common/IIncomePeriod';
import { Constants } from '../../utils/constants';
import { styled } from '@mui/material/styles';
let nextId = -100;

const StyledTable = styled(Table)({
  minWidth: 650,
});

const StyledTableRow = styled(TableRow)({
  '& > *': {
    borderBottom: 'unset',
  },
});

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
});

export interface BudgetPageProps {
  app: IApplication;
  disabled: boolean;
}

export const BudgetPage: FC<BudgetPageProps> = props => {
  const currentApp = useSelector((state: AppState) => state.applications.editingApp);
  const dispatch = useDispatch<ApplicationDispatch>();
  const [openDialog, closeDialog] = useDialog();

  const primary = currentApp && getApplicant(currentApp, Constants.roleApplicantId, true);
  const cobuyer1 = currentApp && getApplicant(currentApp, Constants.roleCoApplicant1Id, true);
  const cobuyer2 = currentApp && getApplicant(currentApp, Constants.roleCoApplicant2Id, true);
  const primaryName = primary?.customer
    ? `: ${primary.customer.firstName} ${primary.customer.lastName}`
    : '';
  const cobuyer1Name = cobuyer1?.customer
    ? `: ${cobuyer1.customer.firstName} ${cobuyer1.customer.lastName}`
    : '';
  const cobuyer2Name = cobuyer2?.customer
    ? `: ${cobuyer2.customer.firstName} ${cobuyer2.customer.lastName}`
    : '';

  const businessRulesEngine = useBusinessRulesEngine();

  const incomeRows = useSelector((state: AppState) => state.applications.editingApp?.applicationBudget?.incomeRows) || [];
  const debtRows = useSelector((state: AppState) => state.applications.editingApp?.applicationBudget?.debtRows) || [];
  const includePrimaryApplicantIncome = useSelector((state: AppState) => state.applications.editingApp?.applicationBudget?.includePrimaryApplicantIncome) || false;
  const includeCoApplicant1Income = useSelector((state: AppState) => state.applications.editingApp?.applicationBudget?.includeCoApplicant1Income) || false;
  const includeCoApplicant2Income = useSelector((state: AppState) => state.applications.editingApp?.applicationBudget?.includeCoApplicant2Income) || false;
  const incomePeriods: IIncomePeriod[] = useSelector(
    (state: AppState) => state.applications.incomePeriods
  );
  const updateApplicationBudget = (appBudget: ApplicationBudget) => {
    dispatch(updateBudget(appBudget, businessRulesEngine));
    onModifyApplicationBudget(
      dispatch,
      appBudget,
      props.app,
      businessRulesEngine
    );
  };

  const updateIncomePrimary = (i: number, value: string) => {
    const appBudget: ApplicationBudget = {
      incomeRows: produce(incomeRows, x => {
        x[i].primaryValue = value;
        if (Number(incomeRows[i].primaryValue || "") !== Number(value || "")) {
          x[i].isAutomaticallyCalculated = false;
        }
      }),
      debtRows,
      includePrimaryApplicantIncome,
      includeCoApplicant1Income,
      includeCoApplicant2Income,
    }
    updateApplicationBudget(appBudget);
  };
  const updateIncomeCobuyer1 = (i: number, value: string) => {
    const appBudget: ApplicationBudget = {
      incomeRows: produce(incomeRows, x => {
        x[i].cobuyer1Value = value;
        if (Number(incomeRows[i].cobuyer1Value || "") !== Number(value || "")) {
          x[i].isAutomaticallyCalculated = false;
        }
      }),
      debtRows,
      includePrimaryApplicantIncome,
      includeCoApplicant1Income,
      includeCoApplicant2Income,
    }
    updateApplicationBudget(appBudget);
  };
  const updateIncomeCobuyer2 = (i: number, value: string) => {
    const appBudget: ApplicationBudget = {
      incomeRows: produce(incomeRows, x => {
        x[i].cobuyer2Value = value;
        if (Number(incomeRows[i].cobuyer2Value || "") !== Number(value || "")) {
          x[i].isAutomaticallyCalculated = false;
        }
      }),
      debtRows,
      includePrimaryApplicantIncome,
      includeCoApplicant1Income,
      includeCoApplicant2Income,
    }
    updateApplicationBudget(appBudget);
  };

  const updateDebtPrimary = (i: number, value: string) => {
    const appBudget: ApplicationBudget = {
      debtRows: produce(debtRows, x => {
        x[i].primaryValue = value;
        if (Number(debtRows[i].primaryValue || "") !== Number(value || "")) {
          x[i].isAutomaticallyCalculated = false;
        }
      }),
      incomeRows,
      includePrimaryApplicantIncome,
      includeCoApplicant1Income,
      includeCoApplicant2Income,
    }
    updateApplicationBudget(appBudget);
  };
  const updateDebtCobuyer1 = (i: number, value: string) => {
    const appBudget: ApplicationBudget = {
      debtRows: produce(debtRows, x => {
        x[i].cobuyer1Value = value;
        if (Number(debtRows[i].cobuyer1Value || "") !== Number(value || "")) {
          x[i].isAutomaticallyCalculated = false;
        }
      }),
      incomeRows,
      includePrimaryApplicantIncome,
      includeCoApplicant1Income,
      includeCoApplicant2Income,
    }
    updateApplicationBudget(appBudget);
  };
  const updateDebtCobuyer2 = (i: number, value: string) => {
    const appBudget: ApplicationBudget = {
      debtRows: produce(debtRows, x => {
        x[i].cobuyer2Value = value;
        if (Number(debtRows[i].cobuyer2Value || "") !== Number(value || "")) {
          x[i].isAutomaticallyCalculated = false;
        }
      }),
      incomeRows,
      includePrimaryApplicantIncome,
      includeCoApplicant1Income,
      includeCoApplicant2Income,
    }
    updateApplicationBudget(appBudget);
  };

  const debtAndIncome = getIncomeAndDebtBreakdownInternal({
    incomeRows,
    debtRows,
    includePrimaryApplicantIncome,
    includeCoApplicant1Income,
    includeCoApplicant2Income
  },
    currentApp?.appCustomers || [],
    undefined,
    currentApp?.includeAllCurrentIncomes,
    incomeRows?.find(r => r.name === 'Additional Income')?.isAutomaticallyCalculated || false);

  const rowTotal = (r: ApplicationBudgetRow) => {
    const total = Math.round((
      (includePrimaryApplicantIncome ? (Number(r.primaryValue) || 0) * 100 : 0)
      + (includeCoApplicant1Income ? (Number(r.cobuyer1Value) || 0) * 100 : 0)
      + (includeCoApplicant2Income ? (Number(r.cobuyer2Value) || 0) * 100 : 0)
    )) / 100;
    return total.toFixed(2);
  };

  const getNextId = () => {
    const current = nextId;
    // Decrement to next id
    nextId = nextId - 1;
    return current;
  };

  const addIncomeRow = () => {
    const appBudget: ApplicationBudget = {
      incomeRows: [
        ...incomeRows,
        {
          id: getNextId(),
          name: '',
          type: 'Income',
          sortOrder: undefined,
          primaryValue: '',
          cobuyer1Value: '',
          cobuyer2Value: '',
          isStandard: false,
        }
      ],
      debtRows,
      includePrimaryApplicantIncome,
      includeCoApplicant1Income,
      includeCoApplicant2Income,
    };
    updateApplicationBudget(appBudget);
  };

  const addDebtRow = () => {
    const appBudget: ApplicationBudget = {
      debtRows: [
        ...debtRows,
        {
          id: getNextId(),
          name: '',
          type: 'Debt',
          sortOrder: undefined,
          primaryValue: '',
          cobuyer1Value: '',
          cobuyer2Value: '',
          isStandard: false,
        }
      ],
      incomeRows,
      includePrimaryApplicantIncome,
      includeCoApplicant1Income,
      includeCoApplicant2Income,
    }
    updateApplicationBudget(appBudget);
  };

  const removeIncomeRow = (i: number) => {
    const appBudget: ApplicationBudget = {
      incomeRows: produce(incomeRows, x => {
        x.splice(i, 1);
      }),
      debtRows,
      includePrimaryApplicantIncome,
      includeCoApplicant1Income,
      includeCoApplicant2Income,
    };
    updateApplicationBudget(appBudget);
  };

  const removeDebtRow = (i: number) => {
    const appBudget: ApplicationBudget = {
      debtRows: produce(debtRows, x => {
        x.splice(i, 1);
      }),
      incomeRows,
      includePrimaryApplicantIncome,
      includeCoApplicant1Income,
      includeCoApplicant2Income,
    };
    updateApplicationBudget(appBudget);
  };

  // If the client has a custom way of calculating submitted income like ACC, it uses these fields
  const primarySubmittedIncomeCustomField = getCustomFieldAllowNull(currentApp!, 'Primary Submitted Income');
  const co1SubmittedIncomeCustomField = getCustomFieldAllowNull(currentApp!, 'Cobuyer1 Submitted Income');
  const co2SubmittedIncomeCustomField = getCustomFieldAllowNull(currentApp!, 'Cobuyer2 Submitted Income');

  // Calculates the income for the primary applicant. Depends on:
  // 1. whether or not the includeAllCurrentIncomes is checked
  // 2. whether or not the includeOtherIncomes is checked
  const getPrimaryApplicantIncome = (): number => {
    // initialize the income value to the primary employment income
    let income = primary?.customer?.customerEmployment?.find(i => i.primaryEmployment)?.income || 0;
    if (currentApp?.includeAllCurrentIncomes) {
      // sum all incomes where isCurrent is true. do not include primary employment because it is already included above.
      income += primary?.customer?.customerEmployment?.filter(i => i.isCurrent && i.primaryEmployment !== true)?.map(i => i.income)?.reduce((a, b) => a + b, 0) || 0;
    }
    return income;
  };

  // Calculates the income for the first cobuyer. Depends on:
  // 1. whether or not the includeAllCurrentIncomes is checked
  // 2. whether or not the includeOtherIncomes is checked
  const getCobuyer1Income = (): number => {
    // initialize the income value to the primary employment income
    let income = cobuyer1?.customer?.customerEmployment?.find(i => i.primaryEmployment)?.income || 0;
    if (currentApp?.includeAllCurrentIncomes) {
      // sum all incomes where isCurrent is true. do not include primary employment because it is already included above.
      income += cobuyer1?.customer?.customerEmployment?.filter(i => i.isCurrent && i.primaryEmployment !== true)?.map(i => i.income)?.reduce((a, b) => a + b, 0) || 0;
    }
    return income;
  };

  // Calculates the income for the second cobuyer. Depends on:
  // 1. whether or not the includeAllCurrentIncomes is checked
  // 2. whether or not the includeOtherIncomes is checked
  const getCobuyer2Income = (): number => {
    // initialize the income value to the primary employment income
    let income = cobuyer2?.customer?.customerEmployment?.find(i => i.primaryEmployment)?.income || 0;
    if (currentApp?.includeAllCurrentIncomes) {
      // sum all incomes where isCurrent is true. do not include primary employment because it is already included above.
      income += cobuyer2?.customer?.customerEmployment?.filter(i => i.isCurrent && i.primaryEmployment !== true)?.map(i => i.income)?.reduce((a, b) => a + b, 0) || 0;
    }
    return income;
  };

  const { data: configuration } = useConfiguration();
  const includeCo2 = configuration?.maxNumberOfApplicants ? configuration?.maxNumberOfApplicants > 2 : false;

  return (
    <div style={{ display: 'flex', flex: 1, flexDirection: 'column', height: 'calc(100vh - 112px)', overflowY: 'auto', overflowX: 'hidden' }}>
      <TableContainer component={Paper} style={{ marginBottom: '50px' }}>
        <StyledTable aria-label="text messages" size="small">
          <TableHead>
            <TableRow
              style={{ pointerEvents: 'auto' }} //always allow them to select and deselect applicants
            >
              <TableCell padding="checkbox" align='right'>
                <Tooltip title='Include all current customer incomes for budget calculations'>
                  <FormControlLabel label={'Include All Current Incomes'}
                    control={
                      <Checkbox
                        {...props}
                        checked={currentApp?.includeAllCurrentIncomes}
                        onChange={(e) => {
                          if (isNotNil(currentApp)) {
                            const updatedApp = produce(currentApp, (currentApp) => {
                              currentApp.includeAllCurrentIncomes = e.target.checked;
                            });
                            dispatch(updateEditingApp(updatedApp, businessRulesEngine));
                          }
                        }}
                      />
                    }
                  />
                </Tooltip>
              </TableCell>
              <TableCell padding="checkbox" align='right'></TableCell>
              <TableCell padding="checkbox">
                <FormControlLabel label={`Include App${primaryName}`}
                  control={
                    <Checkbox
                      {...props}
                      checked={includePrimaryApplicantIncome}
                      onChange={(e) => {
                        const appBudget: ApplicationBudget = {
                          incomeRows,
                          debtRows,
                          includePrimaryApplicantIncome: e.target.checked,
                          includeCoApplicant1Income,
                          includeCoApplicant2Income,
                        };
                        updateApplicationBudget(appBudget);
                      }}
                    />
                  }
                />
              </TableCell>
              <TableCell padding="checkbox">
                <FormControlLabel label={`Include Co1${cobuyer1Name}`}
                  control={
                    <Checkbox
                      {...props}
                      checked={includeCoApplicant1Income}
                      onChange={(e) => {
                        const appBudget: ApplicationBudget = {
                          incomeRows,
                          debtRows,
                          includePrimaryApplicantIncome,
                          includeCoApplicant1Income: e.target.checked,
                          includeCoApplicant2Income,
                        };
                        updateApplicationBudget(appBudget);
                      }}
                    />
                  }
                />
              </TableCell>
              {includeCo2 && <TableCell padding="checkbox">
                <FormControlLabel
                  label={`Include Co2${cobuyer2Name}`}
                  control={
                    <Checkbox
                      {...props}
                      checked={includeCoApplicant2Income}
                      onChange={(e) => {
                        const appBudget: ApplicationBudget = {
                          incomeRows,
                          debtRows,
                          includePrimaryApplicantIncome,
                          includeCoApplicant1Income,
                          includeCoApplicant2Income: e.target.checked,
                        };
                        updateApplicationBudget(appBudget);
                      }}
                    />
                  }
                />
              </TableCell>}
              <TableCell padding="checkbox">
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <StyledTableRow
              style={{ borderBottom: "1px solid rgba(224, 224, 224, 1)" }}
            >
              <TableCell padding="none" align="center">
              </TableCell>
              <TableCell padding="none" align="right" style={{ paddingRight: 10, paddingTop: 10, paddingBottom: 10 }}>
                Submitted Income
              </TableCell>
              <TableCell padding="none">
                <TextField
                  variant="outlined"
                  size="small"
                  margin="none"
                  value={primarySubmittedIncomeCustomField ? primarySubmittedIncomeCustomField.value : getPrimaryApplicantIncome()}
                  InputProps={{
                    inputComponent: NumberFormatCustom as any,
                    readOnly: true,
                  }}
                  inputProps={{
                    style: {
                      textAlign: 'right',
                      color: 'black',
                    }
                  }}
                />
              </TableCell>
              <TableCell padding="none">
                <TextField
                  variant="outlined"
                  size="small"
                  margin="none"
                  value={co1SubmittedIncomeCustomField ? co1SubmittedIncomeCustomField.value : getCobuyer1Income()}
                  InputProps={{
                    inputComponent: NumberFormatCustom as any,
                    readOnly: true,
                  }}
                  inputProps={{
                    style: {
                      textAlign: 'right',
                      color: 'black',
                    }
                  }}
                />
              </TableCell>
              {includeCo2 && <TableCell padding="none">
                <TextField
                  variant="outlined"
                  size="small"
                  margin="none"
                  value={co2SubmittedIncomeCustomField ? co2SubmittedIncomeCustomField.value : getCobuyer2Income()}
                  InputProps={{
                    inputComponent: NumberFormatCustom as any,
                    readOnly: true,
                  }}
                  inputProps={{
                    style: {
                      textAlign: 'right',
                      color: 'black',
                    }
                  }}
                />
              </TableCell>}
              <TableCell padding="none">
              </TableCell>
            </StyledTableRow>
            {incomeRows?.map((r, i) =>
              <StyledTableRow key={i} style={{ margin: '10px 0' }}>
                <TableCell padding="none" align="center" style={{ border: 'none' }}>
                  {!r.isStandard &&
                    <IconButton
                      {...props}
                      title="Remove Income Row"
                      onClick={() => removeIncomeRow(i)}
                      size="small"
                    >
                      <RemoveCircleIcon fontSize="small" />
                    </IconButton>
                  }
                  {r.isStandard && r.budgetRow?.isAutomaticallyCalculated &&
                    <Tooltip title="Automatically Calculate">
                      <Switch
                        {...props}
                        color="primary"
                        checked={r.isAutomaticallyCalculated || false}
                        onChange={(e) => {
                          const appBudget: ApplicationBudget = {
                            incomeRows: produce(incomeRows, x => {
                              x[i].isAutomaticallyCalculated = e.target.checked;
                            }),
                            debtRows,
                            includePrimaryApplicantIncome,
                            includeCoApplicant1Income,
                            includeCoApplicant2Income,
                          };
                          updateApplicationBudget(appBudget);
                        }}
                        size="small"
                      />
                    </Tooltip>
                  }
                </TableCell>
                <TableCell padding="none" align="right" style={{ padding: 10, border: 'none' }}>
                  {r.isStandard && r.name}
                  {!r.isStandard &&
                    <TextField
                      style={{ height: '30px' }}
                      {...props}
                      value={r.name}
                      onChange={(e) => {
                        const appBudget: ApplicationBudget = {
                          incomeRows: produce(incomeRows, x => {
                            x[i].name = e.target.value;
                          }),
                          debtRows,
                          includePrimaryApplicantIncome,
                          includeCoApplicant1Income,
                          includeCoApplicant2Income,
                        };
                        updateApplicationBudget(appBudget);
                      }}
                      placeholder="Custom Income Row"
                      inputProps={{ style: { textAlign: 'right' } }}
                      multiline
                    />
                  }
                </TableCell>
                <TableCell style={{ border: 'none', height: '10px', padding: 2, position: 'relative' }}>
                  <TextField
                    {...props}
                    variant="outlined"
                    size="small"
                    margin="none"
                    value={r.primaryValue || ''}
                    onChange={(e) => updateIncomePrimary(i, e.target.value)}
                    InputProps={{
                      inputComponent: NumberFormatCustom as any,
                    }}
                    inputProps={{ style: { textAlign: 'right' } }}
                  />
                  {r.name === 'Net Income' && !props.disabled && (
                    <MonthlyIncomeCalculator
                      key={i}
                      incomeTypes={[]}
                      itemName='Monthly Income Calculator'
                      onAccept={(value: string) => updateIncomePrimary(i, value)}
                      onCancel={closeDialog}
                      incomePeriods={incomePeriods}
                      buttonTitle='Calculate Monthly Calculator'
                      openDialog={openDialog}
                      closeDialog={closeDialog}
                    />
                  )}
                </TableCell>
                <TableCell padding="none" style={{ border: 'none' }}>
                  <TextField
                    {...props}
                    variant="outlined"
                    size="small"
                    margin="none"
                    value={r.cobuyer1Value || ''}
                    onChange={(e) => updateIncomeCobuyer1(i, e.target.value)}
                    InputProps={{
                      inputComponent: NumberFormatCustom as any,
                    }}
                    inputProps={{ style: { textAlign: 'right' } }}
                  // disabled={r.isAutomaticallyCalculated}
                  // placeholder="0.00"
                  />
                  {r.name === 'Net Income' && !props.disabled && (
                    <MonthlyIncomeCalculator
                      key={i}
                      incomeTypes={[]}
                      itemName='Monthly Income Calculator'
                      onAccept={(value: string) => updateIncomeCobuyer1(i, value)}
                      onCancel={closeDialog}
                      incomePeriods={incomePeriods}
                      buttonTitle='Calculate Monthly Calculator'
                      openDialog={openDialog}
                      closeDialog={closeDialog}
                    />
                  )}
                </TableCell>
                {includeCo2 && <TableCell padding="none" style={{ border: 'none' }}>
                  <TextField
                    {...props}
                    variant="outlined"
                    size="small"
                    margin="none"
                    value={r.cobuyer2Value || ''}
                    onChange={(e) => updateIncomeCobuyer2(i, e.target.value)}
                    InputProps={{
                      inputComponent: NumberFormatCustom as any,
                    }}
                    inputProps={{ style: { textAlign: 'right' } }}
                  // disabled={r.isAutomaticallyCalculated}
                  // placeholder="0.00"
                  />
                  {r.name === 'Net Income' && !props.disabled && (
                    <MonthlyIncomeCalculator
                      key={i}
                      incomeTypes={[]}
                      itemName='Monthly Income Calculator'
                      onAccept={(value: string) => updateIncomeCobuyer2(i, value)}
                      onCancel={closeDialog}
                      incomePeriods={incomePeriods}
                      buttonTitle='Calculate Monthly Calculator'
                      openDialog={openDialog}
                      closeDialog={closeDialog}
                    />
                  )}
                </TableCell>}
                <TableCell padding="none" style={{ border: 'none' }}>
                  <TextField
                    variant="outlined"
                    size="small"
                    margin="none"
                    value={rowTotal(r)}
                    InputProps={{
                      inputComponent: NumberFormatCustom as any,
                      readOnly: true,
                    }}
                    inputProps={{
                      style: {
                        textAlign: 'right',
                        color: 'black',
                      }
                    }}
                  />
                </TableCell>
              </StyledTableRow>
            )}
            <TableRow>
              <TableCell padding="none" align="center">
                <IconButton
                  {...props}
                  title="Add Income Row"
                  onClick={addIncomeRow}
                  size="small"
                >
                  <AddCircleIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
            <TableRow style={{ borderTop: "1px solid rgba(224, 224, 224, 1)" }}>
              <TableCell padding="none"></TableCell>
              <TableCell padding="none" align="right" style={{ paddingRight: 10 }}>
                Total Income
              </TableCell>
              <TableCell padding="none">
                <TextField
                  variant="outlined"
                  size="small"
                  margin="none"
                  value={debtAndIncome.totalIncomePrimary}
                  InputProps={{
                    inputComponent: NumberFormatCustom as any,
                    readOnly: true
                  }}
                  inputProps={{
                    style: {
                      textAlign: 'right',
                      color: 'black',
                      textDecoration: includePrimaryApplicantIncome ? 'none' : 'line-through',
                    }
                  }}
                />
              </TableCell>
              <TableCell padding="none">
                <TextField
                  variant="outlined"
                  size="small"
                  margin="none"
                  value={debtAndIncome.totalIncomeCobuyer1}
                  InputProps={{
                    inputComponent: NumberFormatCustom as any,
                    readOnly: true,
                  }}
                  inputProps={{
                    style: {
                      textAlign: 'right',
                      color: 'black',
                      textDecoration: includeCoApplicant1Income ? 'none' : 'line-through',
                    }
                  }}
                />
              </TableCell>
              {includeCo2 && <TableCell padding="none">
                <TextField
                  variant="outlined"
                  size="small"
                  margin="none"
                  value={debtAndIncome.totalIncomeCobuyer2}
                  InputProps={{
                    inputComponent: NumberFormatCustom as any,
                    readOnly: true,
                  }}
                  inputProps={{
                    style: {
                      textAlign: 'right',
                      color: 'black',
                      textDecoration: includeCoApplicant2Income ? 'none' : 'line-through',
                    }
                  }}
                />
              </TableCell>}
              <TableCell padding="none">
                <TextField
                  variant="outlined"
                  size="small"
                  margin="none"
                  value={debtAndIncome.totalIncomeUsed}
                  InputProps={{
                    inputComponent: NumberFormatCustom as any,
                    readOnly: true,
                  }}
                  inputProps={{
                    style: {
                      textAlign: 'right',
                      color: 'black',
                    }
                  }}
                />
              </TableCell>
            </TableRow>

            {debtRows?.map((r, i) =>
              <StyledTableRow key={i}>
                <TableCell padding="none" align="center" style={{ border: 'none' }}>
                  {!r.isStandard &&
                    <IconButton
                      {...props}
                      title="Remove Debt Row"
                      onClick={() => removeDebtRow(i)}
                      size="small"
                    >
                      <RemoveCircleIcon fontSize="small" />
                    </IconButton>
                  }
                  {r.isStandard && r.budgetRow?.isAutomaticallyCalculated &&
                    <Tooltip title="Automatically Calculate">
                      <Switch
                        {...props}
                        color="primary"
                        checked={r.isAutomaticallyCalculated || false}
                        onChange={(e) => {
                          const appBudget: ApplicationBudget = {
                            incomeRows,
                            debtRows: produce(debtRows, x => {
                              x[i].isAutomaticallyCalculated = e.target.checked;
                            }),
                            includePrimaryApplicantIncome,
                            includeCoApplicant1Income,
                            includeCoApplicant2Income,
                          };
                          updateApplicationBudget(appBudget);
                        }}
                        size="small"
                      />
                    </Tooltip>
                  }
                </TableCell>
                <TableCell align="right" style={{ border: 'none', padding: 2 }}>
                  {r.isStandard && r.name}
                  {!r.isStandard &&
                    <TextField
                      {...props}
                      value={r.name}
                      onChange={(e) => {
                        const appBudget: ApplicationBudget = {
                          incomeRows,
                          debtRows: produce(debtRows, x => {
                            x[i].name = e.target.value;
                          }),
                          includePrimaryApplicantIncome,
                          includeCoApplicant1Income,
                          includeCoApplicant2Income,
                        };
                        updateApplicationBudget(appBudget);
                      }}
                      placeholder="Custom Debt Row"
                      inputProps={{ style: { textAlign: 'right' } }}
                      multiline
                    />
                  }
                </TableCell>
                <TableCell style={{ border: 'none', padding: 2 }}>
                  <TextField
                    {...props}
                    variant="outlined"
                    size="small"
                    margin="none"
                    value={r.primaryValue || ''}
                    onChange={(e) => updateDebtPrimary(i, e.target.value)}
                    InputProps={{
                      inputComponent: NumberFormatCustom as any,
                    }}
                    inputProps={{ style: { textAlign: 'right' } }}
                  />
                </TableCell>
                <TableCell padding="none" style={{ border: 'none' }}>
                  <TextField
                    {...props}
                    variant="outlined"
                    size="small"
                    margin="none"
                    value={r.cobuyer1Value || ''}
                    onChange={(e) => updateDebtCobuyer1(i, e.target.value)}
                    InputProps={{
                      inputComponent: NumberFormatCustom as any,
                    }}
                    inputProps={{ style: { textAlign: 'right' } }}
                  />
                </TableCell>
                {includeCo2 && <TableCell padding="none">
                  <TextField
                    {...props}
                    variant="outlined"
                    size="small"
                    margin="none"
                    value={r.cobuyer2Value || ''}
                    onChange={(e) => updateDebtCobuyer2(i, e.target.value)}
                    InputProps={{
                      inputComponent: NumberFormatCustom as any,
                    }}
                    inputProps={{ style: { textAlign: 'right' } }}
                  />
                </TableCell>}
                <TableCell padding="none" style={{ border: 'none' }}>
                  <TextField
                    variant="outlined"
                    size="small"
                    margin="none"
                    value={rowTotal(r)}
                    InputProps={{
                      inputComponent: NumberFormatCustom as any,
                      readOnly: true,
                    }}
                    inputProps={{
                      style: {
                        textAlign: 'right',
                        color: 'black',
                      }
                    }}
                  />
                </TableCell>
              </StyledTableRow>
            )}
            <TableRow>
              <TableCell padding="none" align="center">
                <IconButton
                  {...props}
                  title="Add Debt Row"
                  onClick={addDebtRow}
                  size="small"
                >
                  <AddCircleIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
            <TableRow style={{ borderTop: "1px solid rgba(224, 224, 224, 1)" }}>
              <TableCell padding="none"></TableCell>
              <TableCell padding="none" align="right" style={{ paddingRight: 10 }}>
                Total Debt
              </TableCell>
              <TableCell padding="none">
                <TextField
                  variant="outlined"
                  size="small"
                  margin="none"
                  value={debtAndIncome.totalDebtPrimary}
                  InputProps={{
                    inputComponent: NumberFormatCustom as any,
                    readOnly: true,
                  }}
                  inputProps={{
                    style: {
                      textAlign: 'right',
                      color: 'black',
                      textDecoration: includePrimaryApplicantIncome ? 'none' : 'line-through',
                    }
                  }}
                />
              </TableCell>
              <TableCell padding="none">
                <TextField
                  variant="outlined"
                  size="small"
                  margin="none"
                  value={debtAndIncome.totalDebtCobuyer1}
                  InputProps={{
                    inputComponent: NumberFormatCustom as any,
                    readOnly: true,
                  }}
                  inputProps={{
                    style: {
                      textAlign: 'right',
                      color: 'black',
                      textDecoration: includeCoApplicant1Income ? 'none' : 'line-through',
                    }
                  }}
                />
              </TableCell>
              {includeCo2 && <TableCell padding="none">
                <TextField
                  variant="outlined"
                  size="small"
                  margin="none"
                  value={debtAndIncome.totalDebtCobuyer2}
                  InputProps={{
                    inputComponent: NumberFormatCustom as any,
                    readOnly: true,
                  }}
                  inputProps={{
                    style: {
                      textAlign: 'right',
                      color: 'black',
                      textDecoration: includeCoApplicant2Income ? 'none' : 'line-through',
                    }
                  }}
                />
              </TableCell>}
              <TableCell padding="none">
                <TextField
                  variant="outlined"
                  size="small"
                  margin="none"
                  value={debtAndIncome.totalDebtUsed}
                  InputProps={{
                    inputComponent: NumberFormatCustom as any,
                    readOnly: true,
                  }}
                  inputProps={{
                    style: {
                      textAlign: 'right',
                      color: 'black',
                    }
                  }}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell padding="none"></TableCell>
              <TableCell padding="none" align="right" style={{ paddingRight: 10 }}>
                Available Income
              </TableCell>
              <TableCell padding="none">
                <TextField
                  variant="outlined"
                  size="small"
                  margin="none"
                  value={subtract(debtAndIncome.availableIncomePrimary, debtAndIncome.availableDebtPrimary)}
                  InputProps={{
                    inputComponent: NumberFormatCustom as any,
                    readOnly: true,
                  }}
                  inputProps={{
                    style: {
                      textAlign: 'right',
                      color: 'black',
                    }
                  }}
                />
              </TableCell>
              <TableCell padding="none">
                <TextField
                  variant="outlined"
                  size="small"
                  margin="none"
                  value={subtract(debtAndIncome.availableIncomeCobuyer1, debtAndIncome.availableDebtCobuyer1)}
                  InputProps={{
                    inputComponent: NumberFormatCustom as any,
                    readOnly: true,
                  }}
                  inputProps={{
                    style: {
                      textAlign: 'right',
                      color: 'black',
                    }
                  }}
                />
              </TableCell>
              {includeCo2 && <TableCell padding="none">
                <TextField
                  variant="outlined"
                  size="small"
                  margin="none"
                  value={subtract(debtAndIncome.availableIncomeCobuyer2, debtAndIncome.availableDebtCobuyer2)}
                  InputProps={{
                    inputComponent: NumberFormatCustom as any,
                    readOnly: true,
                  }}
                  inputProps={{
                    style: {
                      textAlign: 'right',
                      color: 'black',
                    }
                  }}
                />
              </TableCell>}
              <TableCell padding="none">
                <TextField
                  variant="outlined"
                  size="small"
                  margin="none"
                  value={debtAndIncome.totalAvailableIncome}
                  InputProps={{
                    inputComponent: NumberFormatCustom as any,
                    readOnly: true,
                  }}
                  inputProps={{
                    style: {
                      textAlign: 'right',
                      color: 'black',
                      fontWeight: 'bold',
                      padding: '2px'
                    }
                  }}
                />
              </TableCell>
            </TableRow>

          </TableBody>
        </StyledTable>
      </TableContainer>
    </div>
  );
}


